import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetCaseRateRulesetByIdQuery } from "store/queries/pricing";

import { BilateralToggle } from "./components/BilateralToggle";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { CptCodes } from "./components/CptCodes";
import { ExcludedCptCodes } from "./components/ExcludedCptCodes";
import { Header } from "./components/Header";
import { PerDiemToggle } from "./components/PerDiemToggle";
import { ProviderAndPayerCopySection } from "./components/ProviderAndPayerCopySection";
import { RuleName } from "./components/RuleName";
import { SubmitCaseRate } from "./components/SubmitCaseRate";

export function CopyCaseRateRulesPage() {
  useDocumentTitle("Copy Case Rate Rules");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { data: ruleset, isLoading } = useGetCaseRateRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      provider: asDefault
        ? []
        : [
            {
              name: ruleset?.data.provider?.name,
              value: ruleset?.data.provider?.id,
            },
          ],
      payerName: asDefault
        ? []
        : [
            {
              name: ruleset?.data?.payer?.name,
              value: ruleset?.data?.payer?.id,
            },
          ],
      groupName: ruleset?.data.groupName,
      cptCodes: ruleset?.data.cptCodes,
      excludedCptCodes: ruleset?.data.excludedCptCodes,
      isPerDiem: ruleset?.data.isPerDiem ?? false,
      isBilateral: ruleset?.data.isBilateral ?? false,
      isDefault: asDefault,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        provider: asDefault
          ? []
          : [
              {
                name: ruleset?.data.provider?.name,
                value: ruleset?.data.provider?.id,
              },
            ],
        payerName: asDefault
          ? []
          : [
              {
                name: ruleset?.data?.payer?.name,
                value: ruleset?.data?.payer?.id,
              },
            ],
        groupName: ruleset?.data.groupName,
        cptCodes: ruleset?.data.cptCodes,
        excludedCptCodes: ruleset?.data.excludedCptCodes,
        isPerDiem: ruleset?.data.isPerDiem ?? false,
        isBilateral: ruleset?.data.isBilateral ?? false,
        isDefault: asDefault,
      });
    }
  }, [ruleset, methods, asDefault]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <FormProvider {...methods}>
      <Header name={ruleset?.data.groupName} isCaseRate />
      <ProviderAndPayerCopySection
        initialProviders={[
          {
            name: ruleset?.data.provider?.name || "",
            value: ruleset?.data.provider?.id || "",
          },
        ]}
        initialPayers={[
          {
            name: ruleset?.data?.payer?.name || "",
            value: ruleset?.data?.payer?.id || "",
          },
        ]}
      />
      <RuleName />
      <div className="grid">
        <PerDiemToggle />
        <BilateralToggle />
      </div>
      <CptCodes />
      <ExcludedCptCodes />
      <ContractLanguageField ruleType="CASE_RATE" />
      {id && ruleset && <SubmitCaseRate />}
    </FormProvider>
  );
}
